import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getTenant, Tenant, getUnreadNotificationCount, listNotifications, Notification } from '../services/api';
import Sidebar from './Sidebar';
import ConfirmModal from './ConfirmModal';
import NotificationDrawer from './NotificationDrawer';
import Breadcrumb from './Breadcrumb';

/** Dashboard unread notification popup: auto-hides after this duration (no manual close control). */
const SNACKBAR_AUTO_HIDE_MS = 120_000;

const SNACKBAR_AUTO_DISMISSED_IDS_PREFIX = 'snackbar-auto-dismissed-ids';

function snackbarAutoDismissedStorageKey(
  isTenantAdmin: boolean,
  tenantId: string | undefined,
  userId: string | undefined
): string {
  if (isTenantAdmin) {
    return `${SNACKBAR_AUTO_DISMISSED_IDS_PREFIX}:ta:${tenantId ?? ''}`;
  }
  return `${SNACKBAR_AUTO_DISMISSED_IDS_PREFIX}:u:${userId ?? ''}`;
}

function getAutoDismissedNotificationIds(storageKey: string): Set<string> {
  if (typeof sessionStorage === 'undefined') return new Set();
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function addAutoDismissedNotificationId(storageKey: string, id: string): void {
  if (typeof sessionStorage === 'undefined' || !id) return;
  const set = getAutoDismissedNotificationIds(storageKey);
  set.add(id);
  sessionStorage.setItem(storageKey, JSON.stringify([...set]));
}

const DashboardLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout, isSystemAdmin, isTenantAdmin, isTenantLevelAdmin, displayName } = useAuth();
  // Tenant-level Admin logins share tenant-scoped notification + tenant fetch behavior
  const isTenantAdminLike = Boolean(isTenantAdmin || isTenantLevelAdmin);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Initialize sidebar as open on desktop, closed on mobile
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1025;
    }
    return false;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Persist collapsed state across reloads (desktop)
    if (typeof window === 'undefined') {
      return false;
    }
    try {
      const stored = window.localStorage.getItem('sidebarCollapsed');
      if (stored !== null) {
        return stored === 'true';
      }
    } catch {
      // Ignore storage errors and fall back to default
    }
    return false;
  });
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileDropdownRef = useRef<HTMLDivElement>(null);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showUnreadSnackbar, setShowUnreadSnackbar] = useState(false);
  const [renderUnreadSnackbar, setRenderUnreadSnackbar] = useState(false);
  const [snackbarClosing, setSnackbarClosing] = useState(false);
  const [latestUnread, setLatestUnread] = useState<Notification | null>(null);
  const latestUnreadRef = useRef<Notification | null>(null);
  const snackbarAutoDismissStorageKeyRef = useRef('');
  const snackbarTimerRef = useRef<NodeJS.Timeout | null>(null);
  const prevUnreadCountRef = useRef(0);
  const singleUnreadShownRef = useRef<string | null>(null);
  const prevLatestIdRef = useRef<string | null>(null);

  const getShownIds = (): Set<string> => {
    const stored = sessionStorage.getItem('snackbar-single-unread-shown-ids');
    if (!stored) return new Set();
    try {
      const parsed = JSON.parse(stored);
      return new Set(Array.isArray(parsed) ? parsed : []);
    } catch {
      return new Set();
    }
  };

  const addShownId = (id: string) => {
    const set = getShownIds();
    set.add(id);
    sessionStorage.setItem('snackbar-single-unread-shown-ids', JSON.stringify(Array.from(set)));
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1025) {
      // On desktop, toggle between expanded and collapsed
      setSidebarCollapsed((prevCollapsed) => !prevCollapsed);
      setSidebarOpen(true); // Keep sidebar open on desktop, just toggle collapsed state
    } else {
      // On mobile, toggle open/closed
    setSidebarOpen((prevOpen) => !prevOpen);
    }
  };

  // Persist sidebar collapsed state in localStorage so it survives reloads
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem('sidebarCollapsed', sidebarCollapsed ? 'true' : 'false');
    } catch {
      // Ignore storage write errors
    }
  }, [sidebarCollapsed]);

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const handleProfileClick = () => {
    setShowProfileDropdown((prev) => !prev);
  };

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
        setShowProfileDropdown(false);
      }
    };
    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showProfileDropdown]);

  const avatarUrl = user?.avatar || user?.spec?.avatar;
  const userInitial = user?.name?.charAt(0) || user?.username?.charAt(0) || 'U';

  // Fetch tenant data to get hospital name (non-blocking, only for admins)
  useEffect(() => {
    const fetchTenantData = async () => {
      // Only fetch tenant data if user is an admin (has permission to view tenant)
      // Regular users don't need this - they can use tenantName from user object
      if (user?.tenantId && (isSystemAdmin || isTenantAdminLike)) {
        try {
          const response = await getTenant(user.tenantId);
          if (response.data.success && response.data.data) {
            setTenant(response.data.data);
          }
        } catch (error: any) {
          // Don't log errors for non-admin users - this is expected
          // Only log if it's an unexpected error for admins
          if (error.response?.status !== 401) {
            console.error('[DashboardLayout] Failed to fetch tenant data:', error);
          }
        }
      }
    };

    fetchTenantData();
  }, [user?.tenantId, isSystemAdmin, isTenantAdminLike]);

  useEffect(() => {
    latestUnreadRef.current = latestUnread;
  }, [latestUnread]);

  const snackbarAutoDismissStorageKey = snackbarAutoDismissedStorageKey(
    Boolean(isTenantAdminLike),
    user?.tenantId,
    user?.id || user?._id
  );

  useEffect(() => {
    snackbarAutoDismissStorageKeyRef.current = snackbarAutoDismissStorageKey;
  }, [snackbarAutoDismissStorageKey]);

  // Fetch unread notification count
  useEffect(() => {
    // Check if any drawer or modal is open by checking DOM
    const checkIfAnyDrawerOpen = (): boolean => {
      // Check for drawer overlays
      const drawerOverlays = document.querySelectorAll('.drawer-overlay');
      // Check for modal overlays
      const modalOverlays = document.querySelectorAll('.modal-overlay');
      // Check for ConfirmModal (it uses a specific class structure)
      const confirmModals = document.querySelectorAll('[class*="confirm-modal"]');
      
      return drawerOverlays.length > 0 || modalOverlays.length > 0 || confirmModals.length > 0 || notificationDrawerOpen;
    };

    const fetchUnreadCount = async () => {
      // Don't fetch if any drawer or modal is open
      if (checkIfAnyDrawerOpen()) {
        return;
      }

      // Tenant admins see notifications where userId = tenantId, so don't pass userId (backend will use tenantId)
      // Regular users see only their own notifications
      const userId = isTenantAdminLike ? undefined : (user?.id || user?._id);
      try {
        const response = await getUnreadNotificationCount(userId);
        if (response.data.success) {
          setUnreadCount(response.data.data.count);
        }
      } catch (error: any) {
        console.error('Error fetching unread notification count:', error);
      }
    };

    // Always fetch once when component mounts or dependencies change (only if no drawer is open)
    if (!checkIfAnyDrawerOpen()) {
      fetchUnreadCount();
    }

    // Refresh count when notification is read
    const handleNotificationRead = () => {
      if (!checkIfAnyDrawerOpen()) {
        fetchUnreadCount();
      }
    };

    window.addEventListener('notificationRead', handleNotificationRead);

    // Use MutationObserver to watch for drawer/modal changes in DOM
    const observer = new MutationObserver(() => {
      // When DOM changes, check if we should fetch (but don't fetch immediately, let the interval handle it)
      // This ensures we stop polling when drawer opens and resume when it closes
    });

    // Observe the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Only poll if no drawer or modal is open
    let interval: NodeJS.Timeout | null = null;
    
    const startPolling = () => {
      if (interval) {
        clearInterval(interval);
      }
      if (!checkIfAnyDrawerOpen()) {
        // Poll for new notifications every 5 seconds only when no drawer is open
        interval = setInterval(() => {
          if (!checkIfAnyDrawerOpen()) {
            fetchUnreadCount();
          } else {
            // If drawer opened, stop polling
            if (interval) {
              clearInterval(interval);
              interval = null;
            }
          }
        }, 5000);
      }
    };

    // Start polling initially
    startPolling();

    // Also check periodically if drawer state changed (to resume polling when drawer closes)
    const drawerCheckInterval = setInterval(() => {
      if (!checkIfAnyDrawerOpen() && !interval) {
        // Drawer closed, resume polling
        startPolling();
      } else if (checkIfAnyDrawerOpen() && interval) {
        // Drawer opened, stop polling
        clearInterval(interval);
        interval = null;
      }
    }, 500); // Check every 500ms

    return () => {
      window.removeEventListener('notificationRead', handleNotificationRead);
      observer.disconnect();
      if (interval) {
        clearInterval(interval);
      }
      if (drawerCheckInterval) {
        clearInterval(drawerCheckInterval);
      }
    };
  }, [user?.id, user?._id, isTenantAdminLike, notificationDrawerOpen]);

  // Show a snackbar when new unread notifications arrive
  useEffect(() => {
    // Early return if no unread notifications
    if (unreadCount === 0) {
      setShowUnreadSnackbar(false);
      // Do NOT clear snackbar-dismissed-id/count here: on refresh unreadCount is 0 briefly before fetch,
      // and clearing would wipe user's "closed with X" state so snackbar reappears when count loads
      sessionStorage.removeItem('snackbar-last-shown-state');
      if (snackbarTimerRef.current) {
        clearTimeout(snackbarTimerRef.current);
        snackbarTimerRef.current = null;
      }
      prevUnreadCountRef.current = 0;
      setLatestUnread(null);
      return;
    }

    // Get dismissed state early so we respect it even when latestUnread is still loading (e.g. after refresh)
    const dismissedId = sessionStorage.getItem('snackbar-dismissed-id');
    const dismissedCount = sessionStorage.getItem('snackbar-dismissed-count');

    // While latest unread is still loading: if user had dismissed, keep snackbar hidden
    if (!latestUnread && unreadCount > 0) {
      if (dismissedId || (dismissedCount && dismissedCount === unreadCount.toString())) {
        setShowUnreadSnackbar(false);
        prevUnreadCountRef.current = unreadCount;
        return;
      }
    }

    // Determine current notification state identifier
    const currentStateId = latestUnread?._id || `count-${unreadCount}`;
    
    // Get stored values to check for new notifications
    const storedPrevId = sessionStorage.getItem('snackbar-prev-latest-id');
    const storedPrevCount = sessionStorage.getItem('snackbar-prev-count');
    const prevCount = storedPrevCount ? parseInt(storedPrevCount, 10) : prevUnreadCountRef.current;
    const latestId = latestUnread?._id || null;
    
    // Check for new notifications or increased count FIRST
    const hasNewNotification = latestId && latestId !== storedPrevId && latestId !== prevLatestIdRef.current;
    const hasIncreasedCount = unreadCount > prevCount && prevCount >= 0;
    const isFirstLoad = !storedPrevCount && unreadCount > 0;
    const lastShownState = sessionStorage.getItem('snackbar-last-shown-state');
    // dismissedId and dismissedCount already read at top of effect

    // Clear dismissed and last-shown state if we have genuinely new notifications or increased count
    // This must happen BEFORE checking dismissed state, so new notifications can show even if previous ones were dismissed
    if (hasNewNotification || hasIncreasedCount) {
      sessionStorage.removeItem('snackbar-dismissed-id');
      sessionStorage.removeItem('snackbar-dismissed-count');
      sessionStorage.removeItem('snackbar-last-shown-state');
    }
    
    // Now check if snackbar was already dismissed for current notification state (user clicked X)
    if (!hasNewNotification && !hasIncreasedCount && !isFirstLoad) {
      if ((latestUnread?._id && dismissedId === latestUnread._id) ||
          (!latestUnread && dismissedCount === unreadCount.toString())) {
        setShowUnreadSnackbar(false);
        prevUnreadCountRef.current = unreadCount;
        return;
      }
    }

    // Show snackbar only if not dismissed: new notifications, first load, or count increased, or unread and never dismissed
    let shouldShow =
      isFirstLoad ||
      hasNewNotification ||
      hasIncreasedCount ||
      (unreadCount > 0 && !dismissedId && !dismissedCount);

    const latestWasAutoDismissed =
      Boolean(latestId) &&
      getAutoDismissedNotificationIds(snackbarAutoDismissStorageKey).has(latestId as string);
    if (latestWasAutoDismissed && !hasNewNotification && !hasIncreasedCount) {
      shouldShow = false;
    }

    if (!shouldShow) {
      setShowUnreadSnackbar(false);
      // Update stored state even if not showing
      if (latestId) {
        sessionStorage.setItem('snackbar-prev-latest-id', latestId);
        prevLatestIdRef.current = latestId;
      }
      sessionStorage.setItem('snackbar-prev-count', unreadCount.toString());
      prevUnreadCountRef.current = unreadCount;
      return;
    }

    // Show the snackbar until auto-hide (see SNACKBAR_AUTO_HIDE_MS)
    setShowUnreadSnackbar(true);
    sessionStorage.setItem('snackbar-last-shown-state', currentStateId);
    if (latestId) {
      sessionStorage.setItem('snackbar-prev-latest-id', latestId);
      prevLatestIdRef.current = latestId;
    }
    sessionStorage.setItem('snackbar-prev-count', unreadCount.toString());
    if (unreadCount === 1 && latestUnread) {
      singleUnreadShownRef.current = latestUnread._id;
      addShownId(latestUnread._id);
    }

    prevUnreadCountRef.current = unreadCount;
  }, [unreadCount, latestUnread, addShownId, location.pathname, snackbarAutoDismissStorageKey]);

  // Auto-hide after 2 minutes (only depends on showUnreadSnackbar so poll/other updates don't reset the timer)
  useEffect(() => {
    if (!showUnreadSnackbar) {
      if (snackbarTimerRef.current) {
        clearTimeout(snackbarTimerRef.current);
        snackbarTimerRef.current = null;
      }
      return;
    }
    // Auto-hide after 2 min; remember this notification so the snackbar does not show again while it stays latest unread
    snackbarTimerRef.current = setTimeout(() => {
      const id = latestUnreadRef.current?._id;
      if (id) {
        addAutoDismissedNotificationId(snackbarAutoDismissStorageKeyRef.current, id);
      }
      setShowUnreadSnackbar(false);
      snackbarTimerRef.current = null;
    }, SNACKBAR_AUTO_HIDE_MS);
    return () => {
      if (snackbarTimerRef.current) {
        clearTimeout(snackbarTimerRef.current);
        snackbarTimerRef.current = null;
      }
    };
  }, [showUnreadSnackbar]);

  // Control render + closing animation
  useEffect(() => {
    if (showUnreadSnackbar) {
      setRenderUnreadSnackbar(true);
      setSnackbarClosing(false);
      return;
    }

    if (renderUnreadSnackbar) {
      setSnackbarClosing(true);
      const t = setTimeout(() => {
        setRenderUnreadSnackbar(false);
        setSnackbarClosing(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [showUnreadSnackbar, renderUnreadSnackbar]);

  // Fetch the latest unread notification detail when unread exists
  useEffect(() => {
    const fetchLatestUnread = async () => {
      if (unreadCount < 1) {
        setLatestUnread(null);
        return;
      }

      try {
        const userId = isTenantAdminLike ? undefined : (user?.id || user?._id);
        const response = await listNotifications(1, 1, false, undefined, userId);
        if (response.data.success && response.data.data.length > 0) {
          setLatestUnread(response.data.data[0]);
        }
      } catch (error) {
        console.error('Error fetching latest unread notification:', error);
        setLatestUnread(null);
      }
    };

    fetchLatestUnread();
  }, [unreadCount, isTenantAdminLike, user?.id, user?._id]);

  return (
    <div className="dashboard">
      <nav className="navbar meddispatch-navbar">
        <div className="navbar-content">
          <div className="navbar-left">
            <button 
              className="mobile-menu-toggle"
              onClick={toggleSidebar}
              aria-label="Toggle menu"
            >
              ☰
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div className="navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {user?.logo && (
                  <div style={{ 
                    width: '50px', 
                    height: '50px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    flexShrink: 0,
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <img 
                      src={user.logo} 
                      alt="Hospital Logo" 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain',
                        maxWidth: '50px',
                        maxHeight: '50px'
                      }} 
                    />
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span className="navbar-display-name">{displayName}</span>
                  <div className="navbar-breadcrumb">
                  <Breadcrumb />
                  </div>
                </div>
              </div>
              
            </div>
          </div>
          <div className="navbar-right">
            <button 
              className="navbar-icon-btn theme-toggle-btn" 
              onClick={toggleTheme}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button
              className="navbar-icon-btn"
              title="Notifications"
              onClick={() => setNotificationDrawerOpen(true)}
            >
              <span className="icon-bell">🔔</span>
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>
            <div ref={profileDropdownRef} style={{ position: 'relative' }}>
              <button
                type="button"
                className="navbar-user"
                onClick={handleProfileClick}
                title="View profile"
              >
                <div className="user-info">
                  <div className="user-name">{user?.name || user?.username}</div>
                  <div className="user-role">
                    {isSystemAdmin
                      ? 'System Admin'
                      : isTenantLevelAdmin
                      ? 'Admin'
                      : isTenantAdmin
                      ? 'Tenant Admin'
                      : user?.roles && user.roles.length > 0
                      ? user.roles.join(', ')
                      : user?.role || 'No role'}
                  </div>
                </div>
                <div className="user-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="User avatar" />
                  ) : (
                    userInitial
                  )}
                </div>
              </button>
              {showProfileDropdown && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '6px',
                    minWidth: '200px',
                    backgroundColor: 'var(--card-bg, #fff)',
                    border: '1px solid var(--border-color, #e2e8f0)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                    zIndex: 10001,
                    overflow: 'hidden',
                  }}
                >
                  <button
                    onClick={() => {
                      setShowProfileDropdown(false);
                      navigate('/dashboard/profile');
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'var(--text-color, #1f2937)',
                      textAlign: 'left',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg, #f1f5f9)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    Profile
                  </button>
                  {!isSystemAdmin && (
                    <button
                      onClick={() => {
                        setShowProfileDropdown(false);
                        if (location.pathname === '/dashboard/profile') {
                          document.getElementById('change-password')?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                        } else {
                          navigate('/dashboard/profile#change-password');
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: 'var(--text-color, #1f2937)',
                        textAlign: 'left',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg, #f1f5f9)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                      </svg>
                      Change Password
                    </button>
                  )}
                  <div style={{ height: '1px', backgroundColor: 'var(--border-color, #e2e8f0)' }} />
                  <button
                    onClick={() => {
                      setShowProfileDropdown(false);
                      handleLogoutClick();
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      width: '100%',
                      padding: '12px 16px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#ef4444',
                      textAlign: 'left',
                      transition: 'background-color 0.15s',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--hover-bg, #f1f5f9)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="dashboard-main">
        <div className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`} onClick={closeSidebar}></div>
        <Sidebar 
          isOpen={sidebarOpen} 
          isCollapsed={sidebarCollapsed} 
          onClose={closeSidebar} 
        />
        <div className="dashboard-content-wrapper">
          <div className="dashboard-content">{children}</div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to logout? Any unsaved changes will be lost."
        confirmText="Yes, Logout"
        cancelText="Cancel"
        type="warning"
        onConfirm={handleLogoutConfirm}
        onCancel={() => setShowLogoutConfirm(false)}
      />

      {renderUnreadSnackbar && unreadCount > 0 && (
        <div
          className={`notification-snackbar ${snackbarClosing ? 'closing' : 'open'} ${
            latestUnread?.spec.data?.eventType === 'THEFT' ? 'fuel-theft' : 
            latestUnread?.spec.data?.eventType === 'REFILL' ? 'fuel-refill' : 
            latestUnread?.spec.data?.eventType ? 'fuel-event' : ''
          }`}
          style={{
            ...(latestUnread?.spec.data?.eventType === 'THEFT' && {
              borderLeftColor: '#ef4444',
              borderColor: 'rgba(239, 68, 68, 0.65)',
            }),
            ...(latestUnread?.spec.data?.eventType === 'REFILL' && {
              borderLeftColor: '#10b981',
              borderColor: 'rgba(16, 185, 129, 0.65)',
            }),
          }}
          onClick={() => setNotificationDrawerOpen(true)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setNotificationDrawerOpen(true);
            }
          }}
        >
          <div className="notification-snackbar-text">
            {latestUnread ? (
              <>
                <div className="notification-snackbar-text-title">
                  {latestUnread.spec.title}
                </div>
                <div className="notification-snackbar-text-body">
                  {latestUnread.spec.body}
                </div>
                {typeof latestUnread.spec.data?.fuelLevel === 'number' && (
                  <div className="notification-snackbar-text-meta" style={{ marginTop: '4px' }}>
                    IMEI: {latestUnread.spec.data.imei}
                    <> • {latestUnread.spec.data.fuelLevel.toFixed(2)}L</>
                  </div>
                )}
                {unreadCount > 1 && !latestUnread.spec.data?.eventType && (
                  <div className="notification-snackbar-text-meta">
                    {unreadCount} unread notifications
                  </div>
                )}
                {unreadCount > 1 && latestUnread.spec.data?.eventType && (
                  <div className="notification-snackbar-text-meta" style={{ marginTop: '2px' }}>
                    {unreadCount} unread notifications
                  </div>
                )}
              </>
            ) : (
              <div className="notification-snackbar-text-title">
                {`You have ${
                  unreadCount === 1 ? '1 unread notification' : `${unreadCount} unread notifications`
                }`}
              </div>
            )}
          </div>
        </div>
      )}

      <NotificationDrawer
        isOpen={notificationDrawerOpen}
        onClose={() => setNotificationDrawerOpen(false)}
        userId={isTenantAdminLike ? undefined : (user?.id || user?._id)}
        isTenantAdmin={isTenantAdminLike}
      />
    </div>
  );
};

export default DashboardLayout;
